import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getCollection } from '../services/firestoreService';
import { useBranch } from '../context/BranchContext';
import { CLP } from '../utils/finance';

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const str = String(value);
  // Las fechas de reserva vienen como 'YYYY-MM-DD' y deben interpretarse en hora local.
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return new Date(`${str}T00:00:00`);
  }
  const ms = Date.parse(str);
  return Number.isNaN(ms) ? null : new Date(ms);
};

const toDateStr = (value) => {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleDateString('en-CA');
};

const getYearOptions = () => {
  const current = new Date().getFullYear();
  const list = [];
  for (let y = current; y >= 2020; y -= 1) list.push(y);
  return list;
};

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const COLORS = ['#1A3A5C', '#2CB8A0', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6'];

const Kpi = ({ title, value, subtitle }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="h4" fontWeight={800} sx={{ my: 0.5 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export default function AgendaDashboardPage({ onlyAgenda = false }) {
  const { activeBranchId, branches, accessibleBranches } = useBranch();
  const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const todayStr = today.toLocaleDateString('en-CA');
  const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toLocaleDateString('en-CA');

  const [year, setYear] = useState(today.getFullYear());
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(lastOfMonth);
  const [branchFilter, setBranchFilter] = useState(activeBranchId || 'all');
  const [onlineBookings, setOnlineBookings] = useState([]);
  const [clinicalRecords, setClinicalRecords] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [professionalSchedules, setProfessionalSchedules] = useState([]);
  const [sales, setSales] = useState([]);
  const [tiposPrestacion, setTiposPrestacion] = useState([]);
  const [presupuestos, setPresupuestos] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [b, c, p, cl, ps, sl, tp, pr] = await Promise.all([
          getCollection('onlineBookings'),
          getCollection('clinicalRecords'),
          getCollection('professionals'),
          getCollection('clinics'),
          getCollection('professionalSchedules'),
          getCollection('Sales'),
          getCollection('tiposPrestacion'),
          getCollection('presupuestos'),
        ]);
        // Se cargan los datos crudos; el filtro por sucursal se aplica en los useMemo de cada tab.
        setOnlineBookings(b || []);
        setClinicalRecords(c || []);
        setProfessionals(p || []);
        setClinics(cl || []);
        setProfessionalSchedules(ps || []);
        setSales(sl || []);
        setTiposPrestacion(tp || []);
        setPresupuestos(pr || []);
      } catch (err) {
        console.error(err);
        setError('Error cargando datos de agenda.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const bookingMap = useMemo(() => {
    const map = {};
    for (const b of onlineBookings) {
      const key = `${b.petId || b.patientId || ''}::${b.professionalId || ''}::${b.date || ''}`;
      if (!map[key]) map[key] = b;
    }
    return map;
  }, [onlineBookings]);

  // Mapa: professionalId -> Set de branchIds donde tiene horario.
  // Fallback para inferir la sucursal de fichas SOAP sin branchId ni reserva.
  const professionalBranchMap = useMemo(() => {
    const map = {};
    for (const s of professionalSchedules) {
      const pid = s.professionalId || '';
      const bid = s.branchId || '';
      if (!pid || !bid) continue;
      if (!map[pid]) map[pid] = new Set();
      map[pid].add(bid);
    }
    return map;
  }, [professionalSchedules]);

  const professionalMap = useMemo(() => {
    const map = {};
    for (const p of professionals) {
      const fullName = `Dr/a. ${p.nombre || ''} ${p.apellidos || ''}`.trim();
      map[p.id] = fullName;
      // compatibilidad por nombre si falta ID
      map[fullName] = fullName;
      map[`${p.nombre || ''} ${p.apellidos || ''}`.trim()] = fullName;
    }
    return map;
  }, [professionals]);

  const clinicMap = useMemo(() => {
    const map = {};
    for (const c of clinics) {
      map[c.id] = c.name || c.id;
    }
    return map;
  }, [clinics]);

  // Mapa: clinicId -> branchIds (array). Para inferir la sucursal de una clinica.
  const clinicBranchMap = useMemo(() => {
    const map = {};
    for (const c of clinics) {
      const bids = c.branchIds?.length ? c.branchIds : (c.branchId ? [c.branchId] : []);
      map[c.id] = bids;
    }
    return map;
  }, [clinics]);

  // Mapa: professionalId -> clinicName. Usa los clinicIds del profesional y
  // resuelve el nombre de la clinica. Sirve como fallback para inferir la
  // clinica de una ficha SOAP que no tiene reserva asociada.
  const professionalClinicMap = useMemo(() => {
    const map = {};
    for (const p of professionals) {
      const cids = p.clinicIds?.length ? p.clinicIds : (p.clinicId ? [p.clinicId] : []);
      // Usar la primera clinica del profesional como fallback
      if (cids.length > 0) {
        const clinic = clinics.find((c) => c.id === cids[0]);
        if (clinic) map[p.id] = clinic.name || clinic.id;
      }
    }
    return map;
  }, [professionals, clinics]);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || id || 'Sin sucursal';

  const getProfessionalName = (bOrR) => {
    if (bOrR.professionalId && professionalMap[bOrR.professionalId]) {
      return professionalMap[bOrR.professionalId];
    }
    if (bOrR.vetId && professionalMap[bOrR.vetId]) {
      return professionalMap[bOrR.vetId];
    }
    const raw = bOrR.professionalName || bOrR.vetNombre || 'Sin profesional';
    return professionalMap[raw] || raw;
  };

  const getClinicName = (clinicId, clinicNameFallback) => {
    return clinicMap[clinicId] || clinicNameFallback || clinicId || 'Sin clínica';
  };

  const data = useMemo(() => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T23:59:59.999`);

    // Cerramos el periodo en "hoy" si el rango escogido termina después del día actual.
    const periodEnd = end <= today ? end : today;
    if (periodEnd < start) {
      return {
        totalBookings: 0,
        totalRecords: 0,
        completed: 0,
        noShow: 0,
        cancelled: 0,
        enSala: 0,
        attended: 0,
        noShowRate: 0,
        cancellationRate: 0,
        attendanceRate: 0,
        avgDailyBookings: '0.0',
        avgDailyRecords: '0.0',
        avgLeadTime: '0.0',
        monthlyChartData: MESES.map((m) => ({ month: m, citas: 0, citasAnterior: 0, atenciones: 0, atencionesAnterior: 0 })),
        statusPie: [],
        clinicRows: [],
        professionalRows: [],
      };
    }

    const filterBranch = (branchId) => {
      if (!branchFilter || branchFilter === 'all') return true;
      return branchId === branchFilter;
    };

    const filteredBookingsAll = [];
    const filteredBookingsPast = [];
    for (const b of onlineBookings) {
      const d = toDate(b.date);
      if (!d) continue;
      if (d < start || d > end) continue;
      if (!filterBranch(b.branchId)) continue;
      filteredBookingsAll.push(b);
      if (d <= periodEnd) filteredBookingsPast.push(b);
    }

    const filteredRecords = [];
    for (const r of clinicalRecords) {
      const d = toDate(r.createdAt);
      if (!d) continue;
      if (d < start || d > end) continue;
      if (d > periodEnd) continue;

      const dateStr = toDateStr(r.createdAt);
      const bk = bookingMap[`${r.patientId || ''}::${r.vetId || ''}::${dateStr}`];
      const effectiveBranch = bk?.branchId || r.branchId || '';
      if (filterBranch(effectiveBranch)) {
        filteredRecords.push({ ...r, _booking: bk });
        continue;
      }
      // Fallback: inferir sucursal desde el horario del profesional (vetId)
      if (r.vetId && professionalBranchMap[r.vetId]?.has(branchFilter)) {
        filteredRecords.push({ ...r, _booking: bk });
      }
    }

    const totalBookings = filteredBookingsAll.length;
    const totalRecords = filteredRecords.length;

    // Tasas basadas en citas cuya fecha ya pasó (o hoy), no futuras.
    const byStatus = {};
    for (const b of filteredBookingsPast) {
      const s = b.status || 'desconocido';
      byStatus[s] = (byStatus[s] || 0) + 1;
    }
    const completed = byStatus.completed || 0;
    const noShow = byStatus.no_show || 0;
    const cancelled = byStatus.cancelled || 0;
    const enSala = byStatus.en_sala || 0;
    const attended = completed + enSala;
    const pastBookings = filteredBookingsPast.length;
    const noShowRate = pastBookings ? ((noShow / pastBookings) * 100).toFixed(1) : 0;
    const cancellationRate = pastBookings ? ((cancelled / pastBookings) * 100).toFixed(1) : 0;
    const attendanceRate = pastBookings ? ((attended / pastBookings) * 100).toFixed(1) : 0;

    const elapsedDays = Math.max(1, Math.round((periodEnd - start) / (1000 * 60 * 60 * 24)));
    const avgDailyBookings = (pastBookings / elapsedDays).toFixed(1);
    const avgDailyRecords = (totalRecords / elapsedDays).toFixed(1);

    let leadTimeDays = 0;
    let leadCount = 0;
    for (const b of filteredBookingsPast) {
      const created = toDate(b.createdAt);
      const date = toDate(b.date);
      if (created && date) {
        const diff = Math.max(0, Math.round((date - created) / (1000 * 60 * 60 * 24)));
        leadTimeDays += diff;
        leadCount += 1;
      }
    }
    const avgLeadTime = leadCount ? (leadTimeDays / leadCount).toFixed(1) : 0;

    // Gráfico: para el año en curso se usa el periodo ya cerrado (sin meses futuros).
    const monthlyBookings = Array(12).fill(0);
    const monthlyRecords = Array(12).fill(0);
    for (const b of onlineBookings) {
      const d = toDate(b.date);
      if (!d || d.getFullYear() !== year || d > periodEnd) continue;
      if (!filterBranch(b.branchId)) continue;
      monthlyBookings[d.getMonth()] += 1;
    }
    for (const r of clinicalRecords) {
      const d = toDate(r.createdAt);
      if (!d || d.getFullYear() !== year || d > periodEnd) continue;
      if (!filterBranch(r.branchId)) continue;
      monthlyRecords[d.getMonth()] += 1;
    }

    const prevMonthlyBookings = Array(12).fill(0);
    const prevMonthlyRecords = Array(12).fill(0);
    const prevYear = year - 1;
    const pStart = new Date(prevYear, 0, 1, 0, 0, 0);
    const pEnd = new Date(prevYear, 11, 31, 23, 59, 59, 999);
    for (const b of onlineBookings) {
      const d = toDate(b.date);
      if (!d || d < pStart || d > pEnd) continue;
      if (!filterBranch(b.branchId)) continue;
      prevMonthlyBookings[d.getMonth()] += 1;
    }
    for (const r of clinicalRecords) {
      const d = toDate(r.createdAt);
      if (!d || d < pStart || d > pEnd) continue;
      if (!filterBranch(r.branchId)) continue;
      prevMonthlyRecords[d.getMonth()] += 1;
    }

    const monthlyChartData = MESES.map((m, i) => ({
      month: m,
      citas: monthlyBookings[i],
      citasAnterior: prevMonthlyBookings[i],
      atenciones: monthlyRecords[i],
      atencionesAnterior: prevMonthlyRecords[i],
    }));

    const statusPie = Object.entries(byStatus)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    const byClinic = {};
    const byProfessional = {};
    const isAttended = (status) => ['completed', 'en_sala'].includes(status);
    for (const b of filteredBookingsAll) {
      const pName = getProfessionalName(b);
      byProfessional[pName] = byProfessional[pName] || { name: pName, bookings: 0, attendedBookings: 0, records: 0 };
      byProfessional[pName].bookings += 1;
      if (isAttended(b.status)) byProfessional[pName].attendedBookings += 1;

      const cName = getClinicName(b.clinicId, b.clinicName);
      byClinic[cName] = byClinic[cName] || { clinic: cName, bookings: 0, attendedBookings: 0, records: 0 };
      byClinic[cName].bookings += 1;
      if (isAttended(b.status)) byClinic[cName].attendedBookings += 1;
    }
    for (const r of filteredRecords) {
      const bk = r._booking;
      const pName = getProfessionalName(r);
      byProfessional[pName] = byProfessional[pName] || { name: pName, bookings: 0, attendedBookings: 0, records: 0 };
      byProfessional[pName].records += 1;

      const cName = getClinicName(bk?.clinicId, bk?.clinicName);
      byClinic[cName] = byClinic[cName] || { clinic: cName, bookings: 0, attendedBookings: 0, records: 0 };
      byClinic[cName].records += 1;
    }

    const clinicRows = Object.values(byClinic).sort((a, b) => b.records - a.records);
    const professionalRows = Object.values(byProfessional).sort((a, b) => b.records - a.records);

    return {
      totalBookings,
      totalRecords,
      completed,
      noShow,
      cancelled,
      enSala,
      attended,
      noShowRate,
      cancellationRate,
      attendanceRate,
      avgDailyBookings,
      avgDailyRecords,
      avgLeadTime,
      monthlyChartData,
      statusPie,
      clinicRows,
      professionalRows,
    };
  }, [onlineBookings, clinicalRecords, bookingMap, professionalBranchMap, professionalMap, clinicMap, branchFilter, year, startDate, endDate, today]);

  // ── Indicadores de ventas y atenciones cobradas (pestaña "Ventas y Atenciones") ──
  const salesData = useMemo(() => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T23:59:59.999`);
    const periodEnd = end <= today ? end : today;
    if (periodEnd < start) {
      return {
        totalVentas: 0,
        totalAtencionesCobradas: 0,
        ticketPromedio: 0,
        ventasPorClinica: [],
        ventasPorProfesional: [],
        atencionesPorClinicaMedico: [],
        consultasCobradas: { general: 0, especialidad: 0, montoGeneral: 0, montoEspecialidad: 0 },
        tasaDerivacion: 0,
        ventasPorClinicaChart: [],
      };
    }

    const filterBranch = (branchId) => {
      if (!branchFilter || branchFilter === 'all') return true;
      return branchId === branchFilter;
    };

    // Filtrar ventas del periodo
    const filteredSales = sales.filter((s) => {
      const d = toDate(s.paidAt || s.createdAt);
      if (!d) return false;
      if (d < start || d > periodEnd) return false;
      if (!filterBranch(s.branchId)) return false;
      return s.estado !== 'anulada';
    });

    // Mapa: patientId::date -> booking (para obtener clinicId/professionalId)
    const bookingByPatientDate = {};
    for (const b of onlineBookings) {
      const dateStr = toDateStr(b.date);
      const key = `${b.petId || b.patientId || ''}::${dateStr}`;
      if (!bookingByPatientDate[key]) bookingByPatientDate[key] = b;
    }

    // Mapa: tipoPrestacion (lowercase) -> meta (name). Relación uno a uno.
    const goalByTipoMap = {};
    for (const tp of tiposPrestacion) {
      const goalName = (tp.goalName || tp.meta || '').trim();
      const tipoName = (tp.name || '').trim().toLowerCase();
      if (goalName && tipoName) goalByTipoMap[tipoName] = goalName;
    }

    // Set de tipos que tributan a las metas Consulta General y Consulta de Especialidad.
    // Si no hay tipos mapeados a esas metas, se usa fallback por nombre.
    const GOAL_CG = 'consulta general';
    const GOAL_CE = 'consulta de especialidad';
    const tiposMetaConsultas = new Set();
    for (const [tipo, goal] of Object.entries(goalByTipoMap)) {
      const g = goal.toLowerCase().trim();
      if (g === GOAL_CG || g === GOAL_CE) tiposMetaConsultas.add(tipo);
    }
    // Fallback: si no hay tipos mapeados, usar nombres directos
    if (tiposMetaConsultas.size === 0) {
      tiposMetaConsultas.add(GOAL_CG);
      tiposMetaConsultas.add(GOAL_CE);
    }

    // Vincular cada venta con booking/record para obtener clinica y medico.
    // Solo se consideran los ítems que tributan a las metas Consulta General / Consulta de Especialidad.
    // El neto se distribuye proporcionalmente al subtotal de los ítems (igual que Dashboard.js).
    const enrichedSales = filteredSales
      .map((s) => {
        const saleDate = toDate(s.paidAt || s.createdAt);
        const dateStr = saleDate ? toDateStr(saleDate) : '';
        let booking = bookingByPatientDate[`${s.patientId || ''}::${dateStr}`];
        if (!booking && s.patientId) {
          const d = new Date(saleDate);
          d.setDate(d.getDate() - 1);
          booking = bookingByPatientDate[`${s.patientId}::${toDateStr(d)}`];
          if (!booking) {
            d.setDate(d.getDate() + 2);
            booking = bookingByPatientDate[`${s.patientId}::${toDateStr(d)}`];
          }
        }
        const clinicId = booking?.clinicId || s.clinicId || '';
        const clinicName = getClinicName(clinicId, booking?.clinicName);
        const professionalId = booking?.professionalId || s.vetId || '';
        const professionalName = professionalId && professionalMap[professionalId]
          ? professionalMap[professionalId]
          : (booking?.professionalName || s.vetName || 'Sin profesional');

        // Distribución proporcional del neto entre los ítems
        const items = s.items || [];
        const net = Number(s.subtotalNeto) || Number(s.subtotal) || 0;
        const sumItems = items.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0);
        const factor = sumItems > 0 ? net / sumItems : 0;

        let netoRelevante = 0;
        let netoGeneral = 0;
        let netoEspecialidad = 0;
        let countGeneral = 0;
        let countEspecialidad = 0;
        for (const it of items) {
          const tipo = (it.tipoPrestacion || '').trim().toLowerCase();
          if (!tiposMetaConsultas.has(tipo)) continue;
          const value = (Number(it.subtotal) || 0) * factor;
          netoRelevante += value;
          const goal = goalByTipoMap[tipo] || '';
          const g = goal.toLowerCase().trim();
          if (g === GOAL_CG || (!goal && tipo === GOAL_CG)) {
            netoGeneral += value;
            countGeneral += 1;
          } else if (g === GOAL_CE || (!goal && tipo === GOAL_CE)) {
            netoEspecialidad += value;
            countEspecialidad += 1;
          }
        }

        return {
          ...s,
          _clinicName: clinicName,
          _professionalName: professionalName,
          _professionalId: professionalId,
          _neto: netoRelevante,
          _netoGeneral: netoGeneral,
          _netoEspecialidad: netoEspecialidad,
          _countGeneral: countGeneral,
          _countEspecialidad: countEspecialidad,
          _booking: booking,
        };
      })
      .filter((s) => s._neto > 0); // Solo ventas con ítems que tributan a las metas

    const totalVentas = enrichedSales.reduce((sum, s) => sum + s._neto, 0);
    const totalAtencionesCobradas = enrichedSales.length;
    const ticketPromedio = totalAtencionesCobradas > 0 ? totalVentas / totalAtencionesCobradas : 0;

    // Ventas por clínica
    const byClinic = {};
    for (const s of enrichedSales) {
      const key = s._clinicName;
      if (!byClinic[key]) byClinic[key] = { clinic: key, ventas: 0, count: 0, atenciones: 0 };
      byClinic[key].ventas += s._neto;
      byClinic[key].count += 1;
    }

    // Atenciones (fichas SOAP) por clínica
    for (const r of clinicalRecords) {
      const d = toDate(r.createdAt);
      if (!d || d < start || d > periodEnd) continue;
      const dateStr = toDateStr(r.createdAt);
      const bk = bookingByPatientDate[`${r.patientId || ''}::${dateStr}`];
      const effectiveBranch = bk?.branchId || r.branchId || '';
      if (!filterBranch(effectiveBranch)) continue;
      const cName = getClinicName(bk?.clinicId, bk?.clinicName);
      if (!byClinic[cName]) byClinic[cName] = { clinic: cName, ventas: 0, count: 0, atenciones: 0 };
      byClinic[cName].atenciones += 1;
    }

    const ventasPorClinica = Object.values(byClinic)
      .map((row) => ({
        ...row,
        ticketPromedio: row.count > 0 ? row.ventas / row.count : 0,
      }))
      .sort((a, b) => b.ventas - a.ventas);

    // Ventas por profesional
    const byProf = {};
    for (const s of enrichedSales) {
      const key = s._professionalName;
      if (!byProf[key]) byProf[key] = { profesional: key, ventas: 0, count: 0, atenciones: 0 };
      byProf[key].ventas += s._neto;
      byProf[key].count += 1;
    }

    // Atenciones por profesional
    for (const r of clinicalRecords) {
      const d = toDate(r.createdAt);
      if (!d || d < start || d > periodEnd) continue;
      const dateStr = toDateStr(r.createdAt);
      const bk = bookingByPatientDate[`${r.patientId || ''}::${dateStr}`];
      const effectiveBranch = bk?.branchId || r.branchId || '';
      if (!filterBranch(effectiveBranch)) continue;
      const pName = getProfessionalName({ ...r, professionalId: bk?.professionalId });
      if (!byProf[pName]) byProf[pName] = { profesional: pName, ventas: 0, count: 0, atenciones: 0 };
      byProf[pName].atenciones += 1;
    }

    const ventasPorProfesional = Object.values(byProf)
      .map((row) => ({
        ...row,
        ticketPromedio: row.count > 0 ? row.ventas / row.count : 0,
      }))
      .sort((a, b) => b.ventas - a.ventas);

    // Atenciones por clínica y médico (tabla cruzada)
    const byClinicMedico = {};
    for (const r of clinicalRecords) {
      const d = toDate(r.createdAt);
      if (!d || d < start || d > periodEnd) continue;
      const dateStr = toDateStr(r.createdAt);
      const bk = bookingByPatientDate[`${r.patientId || ''}::${dateStr}`];
      const effectiveBranch = bk?.branchId || r.branchId || '';
      if (!filterBranch(effectiveBranch)) continue;
      const cName = getClinicName(bk?.clinicId, bk?.clinicName);
      const pName = getProfessionalName({ ...r, professionalId: bk?.professionalId });
      const key = `${cName}::${pName}`;
      if (!byClinicMedico[key]) byClinicMedico[key] = { clinic: cName, profesional: pName, atenciones: 0, ventas: 0 };
      byClinicMedico[key].atenciones += 1;
    }
    // Sumar ventas a la tabla cruzada
    for (const s of enrichedSales) {
      const key = `${s._clinicName}::${s._professionalName}`;
      if (byClinicMedico[key]) {
        byClinicMedico[key].ventas += s._neto;
      } else {
        byClinicMedico[key] = { clinic: s._clinicName, profesional: s._professionalName, atenciones: 0, ventas: s._neto };
      }
    }
    const atencionesPorClinicaMedico = Object.values(byClinicMedico).sort((a, b) => b.atenciones - a.atenciones);

    // Consultas médicas cobradas (ítems que tributan a Consulta General / Consulta de Especialidad)
    let consultasGeneral = 0;
    let consultasEspecialidad = 0;
    let montoGeneral = 0;
    let montoEspecialidad = 0;
    for (const s of enrichedSales) {
      consultasGeneral += s._countGeneral || 0;
      consultasEspecialidad += s._countEspecialidad || 0;
      montoGeneral += s._netoGeneral || 0;
      montoEspecialidad += s._netoEspecialidad || 0;
    }

    // Tasa de derivación: % de atenciones que generaron venta (cobro)
    const totalAtencionesPeriodo = clinicalRecords.filter((r) => {
      const d = toDate(r.createdAt);
      if (!d || d < start || d > periodEnd) return false;
      const dateStr = toDateStr(r.createdAt);
      const bk = bookingByPatientDate[`${r.patientId || ''}::${dateStr}`];
      const effectiveBranch = bk?.branchId || r.branchId || '';
      return filterBranch(effectiveBranch);
    }).length;
    const tasaDerivacion = totalAtencionesPeriodo > 0
      ? (totalAtencionesCobradas / totalAtencionesPeriodo) * 100
      : 0;

    // Chart data: ventas por clinica
    const ventasPorClinicaChart = ventasPorClinica
      .filter((r) => r.ventas > 0)
      .slice(0, 10)
      .map((r) => ({ name: r.clinic.length > 20 ? r.clinic.substring(0, 20) + '…' : r.clinic, ventas: Math.round(r.ventas) }));

    // Mapa: orderNumber -> presupuesto (para verificar estado de pago)
    const presupuestoByOrderNumber = {};
    for (const pr of presupuestos) {
      const on = pr.orderNumber || '';
      if (on && !presupuestoByOrderNumber[on]) presupuestoByOrderNumber[on] = pr;
    }

    // Atenciones SOAP sin pago de consulta médica.
    // Cruce: booking atendido (status completed/en_sala) cuyo presupuesto asociado
    // (orderNumber = bookingCode) no está pagado (estado !== 'pagado') o
    // booking.paymentStatus === 'no_pagado'.
    // Filtro fijo: clínica Centro Médico (clinicId=54j0Zj09lU7Lri5h6hq7).
    const CLINICA_CENTRO_MEDICO_ID = '54j0Zj09lU7Lri5h6hq7';
    const isAttended = (status) => ['completed', 'en_sala'].includes(status);
    const atencionesSinPago = [];
    for (const b of onlineBookings) {
      if (!isAttended(b.status)) continue;
      const d = toDate(b.date);
      if (!d || d < start || d > periodEnd) continue;
      if (!filterBranch(b.branchId)) continue;
      if (b.clinicId !== CLINICA_CENTRO_MEDICO_ID) continue;
      // Verificar pago: booking.paymentStatus o presupuesto.estado
      const presupuesto = b.bookingCode ? presupuestoByOrderNumber[b.bookingCode] : null;
      const bookingNoPagado = b.paymentStatus === 'no_pagado' || !b.paymentStatus;
      const presupuestoNoPagado = presupuesto ? presupuesto.estado !== 'pagado' : true;
      if (!(bookingNoPagado && presupuestoNoPagado)) continue;
      const pName = getProfessionalName(b);
      atencionesSinPago.push({
        fecha: toDateStr(b.date),
        medico: pName,
        paciente: b.petName || b.patientName || 'Sin paciente',
        clinic: getClinicName(b.clinicId, b.clinicName),
        bookingCode: b.bookingCode || '',
      });
    }
    atencionesSinPago.sort((a, b) => (a.fecha < b.fecha ? 1 : a.fecha > b.fecha ? -1 : 0));

    return {
      totalVentas,
      totalAtencionesCobradas,
      ticketPromedio,
      ventasPorClinica,
      ventasPorProfesional,
      atencionesPorClinicaMedico,
      consultasCobradas: { general: consultasGeneral, especialidad: consultasEspecialidad, montoGeneral, montoEspecialidad },
      tasaDerivacion,
      ventasPorClinicaChart,
      atencionesSinPago,
    };
  }, [sales, clinicalRecords, onlineBookings, presupuestos, professionalMap, clinicMap, professionalClinicMap, branchFilter, startDate, endDate, today, getProfessionalName, getClinicName, tiposPrestacion]);

  const handleExportBookings = () => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T23:59:59.999`);
    const isAttended = (status) => ['completed', 'en_sala'].includes(status);
    const rows = onlineBookings
      .filter((b) => {
        const d = toDate(b.date);
        if (!d) return false;
        if (d < start || d > end) return false;
        if (branchFilter && branchFilter !== 'all' && b.branchId !== branchFilter) return false;
        return true;
      })
      .map((b) => {
        const d = toDate(b.date);
        return {
          ID: b.id || '',
          Fecha: d ? d.toLocaleDateString('es-CL') : '',
          Hora: b.startTime || b.slotTime || '',
          Estado: b.status || '',
          Es_atencion: isAttended(b.status) ? 'Sí' : 'No',
          Sucursal: branchName(b.branchId),
          Clinica: getClinicName(b.clinicId, b.clinicName),
          Profesional: getProfessionalName(b),
          Especialidad: b.specialtyName || '',
          Paciente: b.petName || b.patientName || '',
          Tutor: b.ownerName || '',
          RUT_Tutor: b.ownerRut || '',
          Celular_Tutor: b.ownerCelular || '',
          Especie: b.petSpecies || '',
          Raza: b.petBreed || '',
          Peso: b.petWeight || '',
          Monto: b.amount || 0,
          Estado_Pago: b.paymentStatus || '',
          Codigo_Reserva: b.bookingCode || '',
          Notas: b.notes || '',
          Creado: b.createdAt ? (b.createdAt.toDate ? b.createdAt.toDate().toLocaleString('es-CL') : new Date(b.createdAt).toLocaleString('es-CL')) : '',
        };
      });

    if (rows.length === 0) {
      alert('No hay reservas para el periodo/filtro seleccionado.');
      return;
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Citas');
    XLSX.writeFile(wb, `citas_agenda_${startDate}_${endDate}.xlsx`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>
        Dashboard de Agenda
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="agenda-year-label">Año</InputLabel>
              <Select
                labelId="agenda-year-label"
                value={year}
                label="Año"
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {getYearOptions().map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Fecha inicio"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Fecha término"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="agenda-branch-label">Sucursal</InputLabel>
              <Select
                labelId="agenda-branch-label"
                value={branchFilter}
                label="Sucursal"
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                <MenuItem value="all">Todas las sucursales</MenuItem>
                {(accessibleBranches.length ? accessibleBranches : branches).map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name || b.id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleExportBookings}
              disabled={!onlineBookings.length}
            >
              Descargar Excel
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {!onlyAgenda && (
        <Tabs value={tabIndex} onChange={(e, v) => setTabIndex(v)} sx={{ mb: 2 }}>
          <Tab label="Agenda" />
          <Tab label="Ventas y Atenciones" />
        </Tabs>
      )}

      {tabIndex === 0 && (
      <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi title="Citas agendadas" value={data.totalBookings} subtitle="Reservas programadas en el periodo" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi title="Atenciones SOAP" value={data.totalRecords} subtitle="Fichas clínicas registradas" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi title="Asistencia" value={`${data.attendanceRate}%`} subtitle={`${data.attended} atendidas / ${data.totalBookings} agendadas`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi title="Inasistencia" value={`${data.noShowRate}%`} subtitle={`${data.noShow} no show · ${data.cancelled} canceladas`} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi title="Cancelación" value={`${data.cancellationRate}%`} subtitle="Reservas canceladas" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi title="Prom. citas/día" value={data.avgDailyBookings} subtitle="Promedio diario hasta hoy" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi title="Prom. atenciones/día" value={data.avgDailyRecords} subtitle="Fichas SOAP diarias" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Kpi title="Tiempo de espera" value={`${data.avgLeadTime} días`} subtitle="Promedio desde reserva a cita" />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Serie mensual anualizada: citas y atenciones
            </Typography>
            <Box sx={{ width: '100%', height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthlyChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="citas" fill="#1A3A5C" name={`Citas ${year}`} />
                  <Bar dataKey="citasAnterior" fill="#3B82F6" name={`Citas ${year - 1}`} />
                  <Bar dataKey="atenciones" fill="#2CB8A0" name={`Atenciones ${year}`} />
                  <Bar dataKey="atencionesAnterior" fill="#86efac" name={`Atenciones ${year - 1}`} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Estado de las citas
            </Typography>
            <Box sx={{ width: '100%', height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.statusPie}
                    dataKey="value"
                    nameKey="name"
                    outerRadius={110}
                    label
                  >
                    {data.statusPie.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Atenciones SOAP por clínica
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Clínica</strong></TableCell>
                    <TableCell align="right"><strong>Citas</strong></TableCell>
                    <TableCell align="right"><strong>Citas atendidas</strong></TableCell>
                    <TableCell align="right"><strong>% Asistencia</strong></TableCell>
                    <TableCell align="right"><strong>Fichas SOAP</strong></TableCell>
                    <TableCell align="right"><strong>% Fichas completas</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.clinicRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">Sin datos</TableCell>
                    </TableRow>
                  )}
                  {data.clinicRows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.clinic}</TableCell>
                      <TableCell align="right">{row.bookings}</TableCell>
                      <TableCell align="right">{row.attendedBookings}</TableCell>
                      <TableCell align="right">
                        {row.bookings ? `${Math.min(100, Math.round((row.attendedBookings / row.bookings) * 100))}%` : '–'}
                      </TableCell>
                      <TableCell align="right">{row.records}</TableCell>
                      <TableCell align="right">
                        {row.attendedBookings ? `${Math.min(100, Math.round((row.records / row.attendedBookings) * 100))}%` : '–'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Atenciones por profesional
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Profesional</strong></TableCell>
                    <TableCell align="right"><strong>Citas</strong></TableCell>
                    <TableCell align="right"><strong>Citas atendidas</strong></TableCell>
                    <TableCell align="right"><strong>% Asistencia</strong></TableCell>
                    <TableCell align="right"><strong>Fichas SOAP</strong></TableCell>
                    <TableCell align="right"><strong>% Fichas completas</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.professionalRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">Sin datos</TableCell>
                    </TableRow>
                  )}
                  {data.professionalRows.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="right">{row.bookings}</TableCell>
                      <TableCell align="right">{row.attendedBookings}</TableCell>
                      <TableCell align="right">
                        {row.bookings ? `${Math.min(100, Math.round((row.attendedBookings / row.bookings) * 100))}%` : '–'}
                      </TableCell>
                      <TableCell align="right">{row.records}</TableCell>
                      <TableCell align="right">
                        {row.attendedBookings ? `${Math.min(100, Math.round((row.records / row.attendedBookings) * 100))}%` : '–'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
      </>
      )}

      {tabIndex === 1 && (
      <>
        {/* KPIs principales de ventas */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Kpi title="Ventas consultas" value={CLP(salesData.totalVentas)} subtitle="Neto Consulta General + Especialidad" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Kpi title="Atenciones cobradas" value={salesData.totalAtencionesCobradas} subtitle="Ventas con consultas médicas" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Kpi title="Ticket promedio" value={CLP(Math.round(salesData.ticketPromedio))} subtitle="Venta promedio por atención" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Kpi title="Tasa de derivación" value={`${salesData.tasaDerivacion.toFixed(1)}%`} subtitle="Atenciones que generaron venta" />
          </Grid>
        </Grid>

        {/* Consultas médicas cobradas */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Consultas médicas cobradas
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Tipo consulta</strong></TableCell>
                      <TableCell align="right"><strong>Cantidad</strong></TableCell>
                      <TableCell align="right"><strong>Monto neto</strong></TableCell>
                      <TableCell align="right"><strong>Ticket prom.</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Consulta General</TableCell>
                      <TableCell align="right">{salesData.consultasCobradas.general}</TableCell>
                      <TableCell align="right">{CLP(salesData.consultasCobradas.montoGeneral)}</TableCell>
                      <TableCell align="right">
                        {salesData.consultasCobradas.general > 0
                          ? CLP(Math.round(salesData.consultasCobradas.montoGeneral / salesData.consultasCobradas.general))
                          : '–'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Consulta de Especialidad</TableCell>
                      <TableCell align="right">{salesData.consultasCobradas.especialidad}</TableCell>
                      <TableCell align="right">{CLP(salesData.consultasCobradas.montoEspecialidad)}</TableCell>
                      <TableCell align="right">
                        {salesData.consultasCobradas.especialidad > 0
                          ? CLP(Math.round(salesData.consultasCobradas.montoEspecialidad / salesData.consultasCobradas.especialidad))
                          : '–'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell align="right"><strong>{salesData.consultasCobradas.general + salesData.consultasCobradas.especialidad}</strong></TableCell>
                      <TableCell align="right"><strong>{CLP(salesData.consultasCobradas.montoGeneral + salesData.consultasCobradas.montoEspecialidad)}</strong></TableCell>
                      <TableCell align="right">–</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Gráfico: ventas por clínica */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Ventas consultas por clínica
              </Typography>
              {salesData.ventasPorClinicaChart.length > 0 ? (
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={salesData.ventasPorClinicaChart} layout="vertical" margin={{ top: 8, right: 16, left: 40, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                      <YAxis type="category" dataKey="name" width={120} />
                      <Tooltip formatter={(v) => CLP(v)} />
                      <Bar dataKey="ventas" fill="#1A3A5C" name="Ventas consultas" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  Sin datos de ventas para el periodo seleccionado.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Tabla: ventas por clínica */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Ventas consultas y atenciones por clínica
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Clínica</strong></TableCell>
                      <TableCell align="right"><strong>Ventas consultas</strong></TableCell>
                      <TableCell align="right"><strong>Cobros</strong></TableCell>
                      <TableCell align="right"><strong>Ticket prom.</strong></TableCell>
                      <TableCell align="right"><strong>Atenciones SOAP</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.ventasPorClinica.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center">Sin datos</TableCell></TableRow>
                    )}
                    {salesData.ventasPorClinica.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.clinic}</TableCell>
                        <TableCell align="right">{CLP(Math.round(row.ventas))}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                        <TableCell align="right">{CLP(Math.round(row.ticketPromedio))}</TableCell>
                        <TableCell align="right">{row.atenciones}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Tabla: ventas por profesional */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Ventas consultas y atenciones por médico veterinario
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Profesional</strong></TableCell>
                      <TableCell align="right"><strong>Ventas consultas</strong></TableCell>
                      <TableCell align="right"><strong>Cobros</strong></TableCell>
                      <TableCell align="right"><strong>Ticket prom.</strong></TableCell>
                      <TableCell align="right"><strong>Atenciones SOAP</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.ventasPorProfesional.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center">Sin datos</TableCell></TableRow>
                    )}
                    {salesData.ventasPorProfesional.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.profesional}</TableCell>
                        <TableCell align="right">{CLP(Math.round(row.ventas))}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                        <TableCell align="right">{CLP(Math.round(row.ticketPromedio))}</TableCell>
                        <TableCell align="right">{row.atenciones}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabla: atenciones por clínica y médico */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Atenciones por clínica y médico veterinario
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Clínica</strong></TableCell>
                      <TableCell><strong>Profesional</strong></TableCell>
                      <TableCell align="right"><strong>Atenciones SOAP</strong></TableCell>
                      <TableCell align="right"><strong>Ventas consultas</strong></TableCell>
                      <TableCell align="right"><strong>Ticket prom.</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.atencionesPorClinicaMedico.length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center">Sin datos</TableCell></TableRow>
                    )}
                    {salesData.atencionesPorClinicaMedico.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.clinic}</TableCell>
                        <TableCell>{row.profesional}</TableCell>
                        <TableCell align="right">{row.atenciones}</TableCell>
                        <TableCell align="right">{CLP(Math.round(row.ventas))}</TableCell>
                        <TableCell align="right">
                          {row.atenciones > 0 ? CLP(Math.round(row.ventas / row.atenciones)) : '–'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabla: atenciones SOAP sin pago de consulta médica (Centro Médico) */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Atenciones SOAP sin pago de consulta médica — Centro Médico
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {salesData.atencionesSinPago.length} cita(s) atendida(s) del periodo sin pago de consulta médica.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Fecha atención</strong></TableCell>
                      <TableCell><strong>Médico veterinario</strong></TableCell>
                      <TableCell><strong>Paciente</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.atencionesSinPago.length === 0 && (
                      <TableRow><TableCell colSpan={3} align="center">Sin datos</TableCell></TableRow>
                    )}
                    {salesData.atencionesSinPago.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.fecha}</TableCell>
                        <TableCell>{row.medico}</TableCell>
                        <TableCell>{row.paciente}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </>
      )}
    </Box>
  );
}
